import { createRouter, createWebHistory } from "vue-router";
import { useConfirm } from "primevue/useconfirm";

import { CustomizationRoutes } from "@/domain/Customization/routes/customization.routes";
import { ExternalRoutes } from "@/domain/External/presentation/routes/external.routes";
import { IntegrationsRoutes } from "@/domain/Integrations/shared/routes/integrations.routes";
import { OrganizationsRoutes } from "@/domain/Organizations/presentation/routes/organizations.routes";
import { PlansRoutes } from "@/domain/Plans/presentation/routes/plans.routes";
import { QAToolsRoutes } from "@/domain/QATools/presentation/routes/qatools.routes";
import { UsersRoutes } from "@/domain/Users/presentation/routes/users.routes";

import useAuth from "@/composables/useAuth";
import usePermissions from "@/composables/usePermissions";
import AppLayout from "@/layout/AppLayout.vue";
import { useAppStore } from "@/stores/appStore";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "index",
      redirect: () => {
        return "/admin";
      },
    },
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/auth/Login.vue"),
    },
    {
      path: "/accept-invitation",
      name: "acceptInvitation",
      component: () => import("@/views/auth/AcceptInvitation.vue"),
      meta: { requiresAuth: false },
      props: (route) => {
        return {
          token: route.query.token,
        };
      },
    },
    {
      path: "/forgot-password",
      name: "forgotPassword",
      component: () => import("@/views/auth/ForgotPassword.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/reset-password",
      name: "resetPassword",
      component: () => import("@/views/auth/ResetPassword.vue"),
      meta: { requiresAuth: false },
      props: (route) => {
        return {
          token: route.query.token,
        };
      },
    },
    ...ExternalRoutes,
    {
      path: "/preview/node/:id",
      name: "previewNode",
      component: () => import("@/views/previews/PreviewNode.vue"),
      meta: { requiresAuth: true, permissions: "flows.read" },
    },
    {
      path: "/preview/flow/:id",
      name: "previewFlow",
      component: () => import("@/views/previews/PreviewFlow.vue"),
      meta: { requiresAuth: true, permissions: "flows.read" },
    },
    {
      path: "/flow/:id",
      name: "doFlow",
      component: () => import("@/views/flows/DoFlow.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/admin",
      component: AppLayout,
      children: [
        {
          path: "/admin",
          name: "adminIndex",
          redirect: () => {
            return "/admin/flows";
          },
        },
        {
          path: "flows",
          name: "flows",
          component: () => import("@/views/FlowsPage.vue"),
          meta: { requiresAuth: true, permissions: "flows.read" },
        },
        {
          path: "flow/:id/sessions",
          name: "flowSessions",
          component: () => import("@/views/flow-sessions/FlowSessionsPage.vue"),
          meta: { requiresAuth: true, permissions: "flows.read" },
        },
        {
          path: "flow/:id/sessions/:sessionId/detail",
          name: "flowSessionDetail",
          component: () => import("@/views/flow-sessions/FlowSessionDetailPage.vue"),
          meta: { requiresAuth: true, permissions: "flows.read" },
        },
        {
          path: "flow/:id/sessions/:sessionId/detail/webservices-attempts",
          name: "flowSessionWebserviceAttempts",
          component: () => import("@/views/flow-sessions/FlowWebserviceAttemptsPage.vue"),
          meta: { requiresAuth: true, permissions: "flows.read" },
        },

        ...IntegrationsRoutes,
        ...CustomizationRoutes,
        ...QAToolsRoutes,
        ...UsersRoutes,
        ...OrganizationsRoutes,
        ...PlansRoutes,
        {
          path: "flow-editor/:id",
          name: "flowEditor",
          component: () => import("@/views/EditorPage.vue"),
          meta: { requiresAuth: true, permissions: "flows.read" },
        },
        // FLOW BUILDER
        {
          path: "flow-builder",
          name: "flowBuilder",
          component: () => import("@/views/FlowBuilder.vue"),
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      path: "/debug",
      name: "debug",
      component: () => import("@/views/DebugPage.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/401",
      name: "unauthorized",
      component: () => import("@/views/errors/401Page.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/404",
      name: "notFound",
      component: () => import("@/views/errors/404Page.vue"),
      meta: { requiresAuth: false },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: () => {
        return "/404";
      },
    },
  ],
});

router.beforeEach(async (to) => {
  const { isAuthenticated, getUserData } = useAuth();
  const appStore = useAppStore();
  const confirm = useConfirm();
  const { can } = usePermissions();

  // Close any open confirmation dialogs
  confirm.close();

  // if we are in some auth pages and the user is not authenticated, let the user log in
  if (
    (!isAuthenticated.value && to.name === "login") ||
    to.name === "forgotPassword" ||
    to.name === "resetPassword" ||
    to.name === "acceptInvitation"
  ) {
    return true;
  }

  // we are creating a new session from external URL, let the user in
  if (to.name === "extSessionCreate") {
    return true;
  }

  // we are doing a flow, let the user in
  if (to.name === "doFlow") {
    return true;
  }

  // we are debugging the editor, let the user in
  if (to.name === "debug") {
    return true;
  }

  // if we are in the login page and the user is authenticated, redirect to the admin page
  if (to.name === "login" && isAuthenticated.value) {
    return { name: "adminIndex" };
  }

  // If the user is not authenticated and the route requires authentication, redirect to the login page
  if (!isAuthenticated.value && to.meta.requiresAuth) {
    return { name: "login" };
  }

  await getUserData();

  // if the route contains editor, set the mode to editor
  if (to.name === "flowEditor") {
    appStore.setMode("editor");
  } else {
    appStore.setMode("panel");
    appStore.isMainNavOpen = true;
  }

  // If the url requires permission and the user is authenticated, check if the user has the required permissions
  if (to.meta.permissions) {
    if (!can(to.meta.permissions)) {
      return { name: "unauthorized" };
    }
  }
});

export default router;
